<template>
  <div class="flex flex-col">
    <div class="dropdown show form-dropdown" :class="classList" :style="styleObject">
      <div v-if="isActive" class="flex justify-between">
        <input
          :value="inputValue"
          :placeholder="inputPlaceholder"
          class="form-placeholder dropdown-toggle dropdown-input w-full border-0 bg-transparent p-0 shadow-none outline-none ring-0 focus:outline-transparent focus:ring-0"
          @input="handleInput"
          @focus="handleInputFocus"
          @blur="handleBlur"
        />
        <div class="dropdown-icon">
          <div class="css-arrow"></div>
        </div>
      </div>
      <a
        v-else
        id="dropdownMenuLink"
        class="btn dropdown-toggle text-truncate"
        href="#"
        role="button"
        data-toggle="dropdown"
        aria-haspopup="true"
        aria-expanded="false"
      >
        {{ inputPlaceholder }}
      </a>
      <div class="dropdown-menu" aria-labelledby="dropdownMenuLink"></div>
    </div>
    <div
      v-if="picklistOptions !== undefined && isActive"
      id="options-card"
      :style="styleObject"
      class="mt-4"
    >
      <VueDraggableNext v-model="localPicklistOptions" handle=".drag-handle" class="w-full">
        <div v-for="(pickerOption, index) in localPicklistOptions" :key="index" class="option">
          <div class="align-together flex items-start">
            <Menu05Icon
              v-if="isActive"
              class="drag-handle mr-3 mt-1 h-4 w-4 flex-shrink-0 cursor-move text-gray-500"
            />
            <label
              :id="`option-label-${
                field.id || element.uuid || componentUniqueId || ''
              }-${index}`"
              :key="resetKey"
              :ref="(el) => setLabelRef(el as HTMLElement, index)"
              :for="`${
                field.id || element.uuid || componentUniqueId || ''
              }_${index}`"
              contenteditable="true"
              class="inline-block w-auto flex-grow cursor-text border-0 pr-2 shadow-none outline-none"
              @input="
                (e) => {
                  handleLabelInput(e, index);
                }
              "
              @compositionstart="handleCompositionStart"
              @compositionend="
                (e) => {
                  compositionLabelEndHandler(e, index);
                }
              "
              @keydown="handleSafariCursor"
              @keydown.enter.prevent.stop="handleEnterOnOption(index)"
              @keydown.tab="
                (e) => handleOptionTabNavigation(e, index, localPicklistOptions.length, getLabelRef)
              "
              @keyup.tab.prevent.stop
              @keyup.enter.prevent.stop
              @focus="handleFocus"
              @keyup="handleEditing($event, index)"
            >
              {{ getOptionDisplayText(pickerOption) }}
            </label>
            <Trash01Icon
              v-if="isActive && !(field?.model && field.model.startsWith('custom_objects'))"
              class="ml-2 mt-1 h-4 w-4 flex-shrink-0 cursor-pointer text-gray-500"
              @click="prepareToRemoveOption(index)"
            />
          </div>
        </div>
      </VueDraggableNext>
    </div>
    <div v-if="isActive" class="mt-[-1rem] flex items-center">
      <UIButton id="add-option" text class="mt-4" :disabled="isLastOptionEmpty" @click="addOption">
        <PlusIcon class="mr-1.5 h-5 w-5" />
        {{ t("common.addOption") }}
      </UIButton>
    </div>
    <template v-if="isActive">
      <div
        ref="shortLabelElement"
        :key="resetKey"
        contenteditable="true"
        class="short-label font-inter cursor-text border-0 bg-transparent p-0 text-sm shadow-none focus:outline-none"
        :data-placeholder="t('common.shortLabelPlaceholder')"
        @blur="updateShortLabel"
        @input="handleInputShortLabel"
        @compositionstart="handleCompositionStart"
        @compositionend="compositionEndHandler"
        @keydown="handleSafariCursor"
        @click="(e) => e.stopPropagation()"
        @focus="(e) => e.stopPropagation()"
        v-text="shortLabel"
      ></div>
    </template>
    <template v-else>
      <span v-if="element.shortLabel" class="short-label font-inter break-words text-sm">
        {{ element.shortLabel }}
      </span>
    </template>
  </div>
  <DeleteConfirmationModal
    :show="showDeleteModal"
    @confirm="removeOption"
    @cancel="cancelRemoveOption"
  />
</template>

<script setup lang="ts">
import { useIMEComposition } from "@/composition/useIMEComposition";
import { useAppStore } from "@/store/app";
import {
  fixSafariCursor,
  getCaretOffsetWithin,
  handleOptionTabNavigation,
  handleShortLabelInput as handleShortLabelInputUtil,
  setCaretOffsetWithin,
} from "@/util/methods";
import { Menu05Icon, PlusIcon, Trash01Icon } from "@gohighlevel/ghl-icons/24/outline";
import { UIButton } from "@gohighlevel/ghl-ui";
import { cloneDeep, isEqual } from "lodash";
import { computed, nextTick, ref, watch } from "vue";
import { VueDraggableNext } from "vue-draggable-next";
import { useI18n } from "vue-i18n";
import DeleteConfirmationModal from "./DeleteConfirmationModal.vue";

const { t } = useI18n();
const { handleCompositionStart, handleCompositionEnd, withIMECheck, resetKey } =
  useIMEComposition();
interface OptionType {
  [key: string]: any;
}

const props = defineProps({
  type: {
    type: String,
    default: "input",
  },
  placeholder: {
    type: String,
    default: "",
  },
  picklistOptions: {
    type: Array as () => (string | OptionType)[],
    default() {
      return [];
    },
  },
  field: {
    type: Object,
    default() {
      return {};
    },
  },
  styleObject: {
    type: Object,
    default() {
      return {};
    },
  },
  classList: {
    type: Array,
    default: () => {
      return [];
    },
  },
  isActive: {
    type: Boolean,
    default: false,
  },
  element: {
    type: Object,
    default: () => {
      return {
        fontSize: "14px",
      };
    },
  },
  customFieldCreated: {
    type: Boolean,
    default: false,
  },
});

const componentUniqueId = ref(null);
if(!componentUniqueId.value && !props.field.id && !props.element.uuid) {
  componentUniqueId.value = Date.now().toString();
}

const emit = defineEmits(["update:placeholder", "update:shortLabel", "update:picklist-options"]);

const inputPlaceholder = ref(props.placeholder);
const inputValue = ref("");
const shortLabel = ref(props.element.shortLabel || "");
const store = useAppStore();

// Add local state for the draggable component
const localPicklistOptions = ref([...props.picklistOptions]);

// Add bidirectional watchers with flag to prevent recursive updates
const isInternalUpdate = ref(false);
const isSyncingOptionsFromProps = ref(false);

watch(
  () => props.picklistOptions,
  (newVal) => {
    if (
      !isInternalUpdate.value &&
      !isEqual(newVal, localPicklistOptions.value)
    ) {
      localPicklistOptions.value = JSON.parse(JSON.stringify(newVal));
      isSyncingOptionsFromProps.value = true;
      // Nested nextTick to ensure the focus is restored to the sidebar
      nextTick(() => {
        nextTick(() => {
          isSyncingOptionsFromProps.value = false;
        });
      });
    }
    isInternalUpdate.value = false;
  },
  { deep: true, immediate: true }
);

watch(
  () => localPicklistOptions.value,
  (newVal) => {
    if (!isEqual(newVal, props.picklistOptions)) {
      isInternalUpdate.value = true;
      emit("update:picklist-options", cloneDeep(newVal));
    }
  },
  { deep: true }
);

watch(
  () => props.placeholder,
  (newVal) => {
    inputPlaceholder.value = newVal;
  }
);

watch(
  () => props.element.shortLabel,
  (newVal) => {
    shortLabel.value = newVal || "";
  }
);

/**
 * Gets the display text for an option which could be a string or an object with a label property
 *
 * @param option - The option to get the display text for
 * @returns The display text for the option
 */
const getOptionDisplayText = (option: string | OptionType): string => {
  if (typeof option === "string") {
    return option;
  }
  return option.label || "";
};

const updateOptionLabel = (event: Event, index: number) => {
  const target = event.target as HTMLElement;
  const text = target.innerText.replace(/\u200B/g, "");
  const caret = getCaretOffsetWithin(target);

  const newOptions = [...localPicklistOptions.value];
  const currentOption = newOptions[index];

  if (typeof currentOption === "string") {
    newOptions[index] = text;
  } else {
    newOptions[index] = { ...currentOption, label: text };
  }

  localPicklistOptions.value = newOptions;
  nextTick(() => {
    const el = labelRefs.value[index] || target;
    setCaretOffsetWithin(el, caret);
  });
};

const indexToRemove = ref<number | null>(null);
const showDeleteModal = ref(false);

const prepareToRemoveOption = (index: number) => {
  if (props.picklistOptions.length > 1) {
    if (props.customFieldCreated) {
      indexToRemove.value = index;
      showDeleteModal.value = true;
    } else {
      const newOptions = [...localPicklistOptions.value];
      newOptions.splice(index, 1);
      localPicklistOptions.value = newOptions;
    }
  }
};

const removeOption = () => {
  if (indexToRemove.value !== null) {
    const newOptions = [...localPicklistOptions.value];
    newOptions.splice(indexToRemove.value, 1);
    localPicklistOptions.value = newOptions;
  }
  showDeleteModal.value = false;
};

const cancelRemoveOption = () => {
  indexToRemove.value = null;
  showDeleteModal.value = false;
};

const isLastOptionEmpty = computed(() => {
  const lastOption = props.picklistOptions[props.picklistOptions.length - 1];
  if (typeof lastOption === "string") {
    return lastOption.trim() === "";
  }
  return lastOption?.label?.trim() === "";
});

const addOption = () => {
  addOptionAt((localPicklistOptions.value?.length ?? 1) - 1);
};

// Keep refs for option labels to focus new ones
const labelRefs = ref<HTMLElement[]>([]);
const setLabelRef = (el: HTMLElement | null, index: number) => {
  if (el) {
    labelRefs.value[index] = el;
  }
};
const getLabelRef = (i: number): HTMLElement | null => labelRefs.value[i] || null;

const addOptionAt = (index: number) => {
  const newOptions = [...localPicklistOptions.value];
  const insertAt = Math.min(Math.max(index + 1, 0), newOptions.length);
  // Match the format of existing options (string or object with label)
  const lastOption = newOptions[newOptions.length - 1];
  if (typeof lastOption === "string") {
    newOptions.splice(insertAt, 0, `Option ${newOptions.length + 1}`);
  } else {
    newOptions.splice(insertAt, 0, {
      label: `Option ${newOptions.length + 1}`,
      calculatedValue: "",
    });
  }
  localPicklistOptions.value = newOptions;
  nextTick(() => {
    const focusLabel =
      labelRefs.value[insertAt] ||
      (document.getElementById(
        `option-label-${
          props.field.id ||
          props.element.uuid ||
          componentUniqueId.value ||
          ""
        }-${insertAt}`
      ) as HTMLElement | null);
    if (focusLabel) {
      focusLabel.focus();
      const range = document.createRange();
      range.selectNodeContents(focusLabel);
      const sel = window.getSelection();
      if (sel) {
        sel.removeAllRanges();
        sel.addRange(range);
      }
    }
  });
};

const handleFocus = (event: FocusEvent) => {
  const target = event.target as HTMLElement;
  const related = event.relatedTarget as HTMLElement | null; // the element that lost focus

  const cameFromFormInput =
    related && ["INPUT"].includes(related.tagName || "");
  if (cameFromFormInput && isSyncingOptionsFromProps.value) {
    nextTick(() => related?.focus());
    return;
  }

  const selection = window.getSelection();
  if (selection) {
    selection.removeAllRanges();
  }

  const sanitized = (target.textContent || "").replace(/\u200B/g, "").trim();
  if (!sanitized) {
    let textNode = target.firstChild as Text | null;
    if (!textNode || textNode.nodeType !== Node.TEXT_NODE) {
      target.textContent = "";
      textNode = document.createTextNode("\u200B");
      target.appendChild(textNode);
    }
    if (!textNode.textContent) {
      textNode.textContent = "\u200B";
    }
    const range = document.createRange();
    range.setStart(textNode, 0);
    range.collapse(true);
    selection?.addRange(range);
    return;
  }

  const range = document.createRange();
  range.selectNodeContents(target);
  selection?.addRange(range);
};

const updateShortLabel = () => emit("update:shortLabel", shortLabel.value);

const handleEditing = (event: KeyboardEvent, index: number) => {
  const target = event.target as HTMLElement;

  if (target.textContent?.trim() === "") {
    if (target.textContent) {
      target.textContent = "";

      const range = document.createRange();
      range.setStart(target, 0);
      range.collapse(true);

      const selection = window.getSelection();
      if (selection) {
        selection.removeAllRanges();
        selection.addRange(range);
      }
    }
  }
};

const handleInput = (event: Event) => {
  const target = event.target as HTMLInputElement;
  inputValue.value = target.value;
  inputPlaceholder.value = target.value;
  emit("update:placeholder", inputPlaceholder.value);
};

const handleInputFocus = () => {
  inputValue.value = inputPlaceholder.value;
};

const handleBlur = (event: Event) => {
  inputValue.value = "";
  store.addToHistory();
};

const handleSafariCursor = (e: Event) => {
  const isKeyDown = e.type === "keydown";
  fixSafariCursor(e, isKeyDown);
};

const rawInputShortLabelHandler = (e: Event) => {
  handleShortLabelInputUtil(e, shortLabel, updateShortLabel);
};

const handleInputShortLabel = withIMECheck(rawInputShortLabelHandler);
const compositionEndHandler = handleCompositionEnd(rawInputShortLabelHandler);

const rawHandleLabelInput = (e: Event, index: number) => {
  updateOptionLabel(e, index);
  handleSafariCursor(e);
};

const handleLabelInput = withIMECheck(rawHandleLabelInput);
const compositionLabelEndHandler = handleCompositionEnd(rawHandleLabelInput);
const handleEnterOnOption = (index: number) => {
  addOptionAt(index);
};
</script>

<style>
.form-dropdown {
  padding: 15px 20px;
  border-radius: 5px;
}
.form-dropdown a {
  padding: 0;
}
.show {
  display: block;
}

.text-truncate {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
  display: block;
}

@font-face {
  font-family: "Magicons";
  src: url("./../../assets/icons/Magicons.eot?g0nnmd");
  src: url("./../../assets/icons/Magicons.eot?g0nnmd#iefix") format("embedded-opentype"),
    url("./../../assets/icons/Magicons.ttf?g0nnmd") format("truetype"),
    url("./../../assets/icons/Magicons.woff?g0nnmd") format("woff");
  font-weight: normal;
  font-style: normal;
}

.btn.dropdown-toggle:after {
  content: "\25BC";
  display: block;
  margin: 0;
  position: absolute;
  top: 50%;
  right: 14px;
  -webkit-transform: translateY(-50%);
  transform: translateY(-50%) scaleY(0.5);
  font-family: "Magicons" !important;
  speak: none;
  font-style: normal;
  font-weight: normal;
  font-variant: normal;
  text-transform: none;
  line-height: 1;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  width: 11px;
  height: 10px;
  font-size: 12px;
  border: none !important;
}

#options-card > div {
  padding: 0.8rem;
}

.dropdown-icon {
  display: inline-flex;
  align-items: center;
  margin-right: -5px;
  margin-top: 3px;
}

.css-arrow {
  width: 0;
  height: 0;
  border-left: 5px solid transparent;
  border-right: 5px solid transparent;
  border-top: 5px solid #8c8c8c;
}

.dropdown-icon:active .css-arrow,
.dropdown-icon:focus .css-arrow,
.dropdown-icon:hover .css-arrow,
.active .css-arrow {
  border-left: 5px solid transparent !important;
  border-right: 5px solid transparent !important;
  border-top: 5px solid #8c8c8c !important;
}

.short-label {
  background-color: transparent;
  margin-top: 4px;
  font-size: 14px;
  color: #4b5563;
}

.short-label:empty:before {
  content: attr(data-placeholder);
}
</style>
